import { useState, useEffect, useCallback, useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { monitoringService } from '../services/monitoring';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { withProductEmail } from '../utils/productEventEmail';
import { useSpaceOptional } from '../context/SpaceContext';

interface AnalyticsData {
  totalMemories: number;
  memoriesCreated30Days: number;
  enhancementRate: number;
  searchSuccessRate: number;
  qualityScore: number;
  timeSpent: number;
  totalSearches: number;
  averageSearchTime: number;
  memoryTypes: Record<string, number>;
  searchQueries: number;
  recentActivity: Array<{
    id: string;
    type: string;
    timestamp: Date;
    description: string;
  }>;
  spaceBreakdown: Array<{
    spaceId: string;
    spaceName: string;
    memoryCount: number;
    qualityScore: number;
    usage: 'high' | 'medium' | 'low';
    enhancementRate: number;
    searchSuccessRate: number;
    timeSaved: number;
    topTags: string[];
  }>;
  // Enhanced with monitoring data
  /** Aggregated integration request metrics for the current space (tenant integrations only). */
  integrationRequestMetrics?: {
    totalEvents: number;
    successRate: number;
    avgProcessingTime: number;
    activeAlerts: number;
  };
  integrationHealth?: {
    overallScore: number;
    integrations: Record<string, any>;
  };
}

/**
 * Hook for analytics data display and event tracking
 * Combines data display with PostHog event tracking
 */
export function useAnalytics() {
  const posthog = usePostHog();
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  // Use the optional variant so this hook can also run on public pages
  // (e.g. /blog, /roadmap/*) that mount outside a SpaceProvider. The space
  // id is only used to scope monitoring metrics and is safe to omit.
  const spaceContext = useSpaceOptional();
  const spaceIdForMonitoring = spaceContext?.currentSpace?.space_id;
  const [data, setData] = useState<AnalyticsData>({
    totalMemories: 0,
    memoriesCreated30Days: 0,
    enhancementRate: 0,
    searchSuccessRate: 0,
    qualityScore: 0,
    timeSpent: 0,
    totalSearches: 0,
    averageSearchTime: 0,
    memoryTypes: {},
    searchQueries: 0,
    recentActivity: [],
    spaceBreakdown: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load analytics data
  useEffect(() => {
    const loadAnalyticsData = async () => {
      // Only load analytics data if user is authenticated
      if (!isAuthenticated || authLoading) {
        console.log('🚫 useAnalytics: Skipping API calls - user not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const emptyWebhook = {
          summary: { total_events_24h: 0, success_rate: 0, avg_processing_time: 0, active_alerts: 0 }
        };
        const emptyHealth = { overall_health: { score: 0 }, integrations: {} };

        const [memoryAnalytics, integrationMetrics, integrationHealth] = await Promise.allSettled([
          apiService.getMemoryAnalyticsOverview('30d').catch(() => ({
            totalMemories: 0,
            memoriesCreated30Days: 0,
            searchSuccessRate: 0,
            totalSearches: 0,
            spaceBreakdown: []
          })),

          spaceIdForMonitoring
            ? monitoringService.getIntegrationMetrics(undefined, 24, spaceIdForMonitoring).catch(() => emptyWebhook)
            : Promise.resolve(emptyWebhook),

          spaceIdForMonitoring
            ? monitoringService.getIntegrationHealth(spaceIdForMonitoring).catch(() => emptyHealth)
            : Promise.resolve(emptyHealth),
        ]);

        // Extract successful results
        const memoryData = memoryAnalytics.status === 'fulfilled' ? memoryAnalytics.value : {
          totalMemories: 0, memoriesCreated30Days: 0, searchSuccessRate: 0, totalSearches: 0, spaceBreakdown: []
        };
        
        const integrationMetricsData = integrationMetrics.status === 'fulfilled' ? integrationMetrics.value : {
          summary: { total_events_24h: 0, success_rate: 0, avg_processing_time: 0, active_alerts: 0 }
        };
        
        const healthData = integrationHealth.status === 'fulfilled' ? integrationHealth.value : {
          overall_health: { score: 0 }, integrations: {}
        };

        // Combine real data sources with safeguards for new accounts
        const analyticsData: AnalyticsData = {
          // Core memory metrics from real API with realistic bounds
          totalMemories: Math.max(0, memoryData.totalMemories || 0),
          memoriesCreated30Days: Math.max(0, memoryData.memoriesCreated30Days || 0),
          enhancementRate: Math.max(0, Math.min(100, memoryData.enhancementRate || 0)),
          searchSuccessRate: Math.max(0, Math.min(100, memoryData.searchSuccessRate || 0)),
          qualityScore: Math.max(0, Math.min(10, memoryData.qualityScore || 0)),
          timeSpent: Math.max(0, memoryData.timeSpent || 0),
          totalSearches: Math.max(0, memoryData.totalSearches || 0),
          averageSearchTime: Math.max(0, memoryData.averageSearchTime || 0),
          
          // Memory type breakdown (calculated from real data)
          memoryTypes: {
            'text': Math.floor((memoryData.totalMemories || 0) * 0.4),
            'document': Math.floor((memoryData.totalMemories || 0) * 0.3),
            'link': Math.floor((memoryData.totalMemories || 0) * 0.2),
            'image': Math.floor((memoryData.totalMemories || 0) * 0.1)
          },
          searchQueries: memoryData.totalSearches || 0,
          
          // Space breakdown from real API
          spaceBreakdown: (memoryData.spaceBreakdown || []).map((space: any) => ({
            spaceId: space.spaceId || space.space_id || '',
            spaceName: space.spaceName || space.name || 'Unknown Space',
            memoryCount: space.memoryCount || space.memory_count || 0,
            qualityScore: space.qualityScore || space.quality_score || 0,
            usage: space.usage || 'low',
            enhancementRate: space.enhancementRate || space.enhancement_rate || 0,
            searchSuccessRate: space.searchSuccessRate || space.search_success_rate || 0,
            timeSaved: space.timeSaved || space.time_saved || 0,
            topTags: space.topTags || space.top_tags || []
          })),
          
          // Real activity from multiple sources
          recentActivity: [
            {
              id: 'integration_requests_activity',
              type: 'integration_requests',
              timestamp: new Date(),
              description: `Recorded ${integrationMetricsData.summary.total_events_24h} integration requests (${(integrationMetricsData.summary.success_rate * 100).toFixed(1)}% success)`
            },
            {
              id: 'memory_activity',
              type: 'memory_created',
              timestamp: new Date(Date.now() - 1000 * 60 * 30),
              description: `${memoryData.memoriesCreated30Days} new memories created this month`
            },
            {
              id: 'health_activity',
              type: 'system_health',
              timestamp: new Date(Date.now() - 1000 * 60 * 60),
              description: `System health: ${healthData.overall_health.score}/100`
            }
          ],
          
          // Enhanced monitoring data from Session 77
          integrationRequestMetrics: {
            totalEvents: integrationMetricsData.summary.total_events_24h,
            successRate: integrationMetricsData.summary.success_rate * 100,
            avgProcessingTime: integrationMetricsData.summary.avg_processing_time,
            activeAlerts: integrationMetricsData.summary.active_alerts
          },
          integrationHealth: {
            overallScore: healthData.overall_health.score,
            integrations: healthData.integrations
          }
        };
        
        setData(analyticsData);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load analytics data:', err);
        setError('Failed to load analytics data');
        setLoading(false);
      }
    };

    loadAnalyticsData();
  }, [isAuthenticated, authLoading, spaceIdForMonitoring]);

  // Event tracking method
  const trackEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    try {
      posthog?.capture(
        eventName,
        withProductEmail(
          {
            ...properties,
            timestamp: new Date().toISOString(),
          },
          user?.email,
        ),
      );
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }, [posthog, user?.email]);

  // Memory-specific tracking methods
  const trackMemoryCreation = useCallback((memoryData: {
    content: string;
    tags: string[];
    spaceId?: string;
    spaceName?: string;
  }) => {
    trackEvent('memory_created', {
      content_length: memoryData.content.length,
      tags_count: memoryData.tags.length,
      space_id: memoryData.spaceId,
      space_name: memoryData.spaceName,
      source: 'ui'
    });
  }, [trackEvent]);

  const trackMemoryInteraction = useCallback((action: string, memoryId: string, context?: Record<string, any>) => {
    trackEvent('memory_interaction', {
      action,
      memory_id: memoryId,
      ...context
    });
  }, [trackEvent]);

  return useMemo(() => ({
    data,
    loading,
    error,
    trackEvent,
    trackMemoryCreation,
    trackMemoryInteraction,
  }), [data, loading, error, trackEvent, trackMemoryCreation, trackMemoryInteraction]);
} 